// src/services/client-errors.service.js

const MAX_BUFFERED_ERRORS = 50;
const MAX_STRING_LENGTH = 1000;
const MAX_STACK_LENGTH = 4000;
const MAX_DEPTH = 4;
const MAX_COLLECTION_ITEMS = 30;

// Одна и та же ошибка в цикле не должна вытеснить из лога всё остальное.
const REPEAT_WINDOW_MS = 5000;
const MAX_TRACKED_SIGNATURES = 200;

const SECRET_KEY_PATTERN = /token|authorization|password|passwd|secret|credential|cookie|api_?key|access_?key|signature/i;
const BEARER_PATTERN = /Bearer\s+[\w.~+/=-]+/gi;
const JWT_PATTERN = /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g;

const REDACTED = '[redacted]';

const originalConsoleError = console.error.bind(console);

const recentSignatures = new Map();
let bufferedErrors = [];
let errorListener = null;
let installed = false;
let reporting = false;

function sanitizeString(value, maxLength = MAX_STRING_LENGTH) {
    const redacted = value.replace(BEARER_PATTERN, REDACTED).replace(JWT_PATTERN, REDACTED);

    if (redacted.length <= maxLength) return redacted;

    return `${redacted.slice(0, maxLength)}…[trimmed ${redacted.length - maxLength}]`;
}

function stripUrlQuery(url) {
    return typeof url === 'string' ? url.split(/[?#]/)[0] : url;
}

function serializeError(error) {
    return {
        name: error.name,
        message: sanitizeString(String(error.message)),
        code: error.code,
        // Весь объект ошибки не сериализуем: в axios-ошибке лежит config с заголовком Authorization.
        status: error.response?.status,
        stack: typeof error.stack === 'string' ? sanitizeString(error.stack, MAX_STACK_LENGTH) : undefined,
        cause: error.cause instanceof Error
            ? { name: error.cause.name, message: sanitizeString(String(error.cause.message)) }
            : undefined,
    };
}

export function serializeValue(value, depth = 0, seen = new WeakSet()) {
    if (typeof value === 'string') return sanitizeString(value);
    if (typeof value === 'function') return '[function]';
    if (typeof value === 'bigint' || typeof value === 'symbol') return String(value);
    if (!value || typeof value !== 'object') return value;

    if (value instanceof Error) return serializeError(value);
    if (typeof Node !== 'undefined' && value instanceof Node) return `[${value.nodeName}]`;
    if (value instanceof ArrayBuffer || ArrayBuffer.isView(value) || value instanceof Blob) return '[binary]';
    if (seen.has(value)) return '[circular]';
    if (depth >= MAX_DEPTH) return '[too-deep]';

    seen.add(value);

    if (Array.isArray(value)) {
        return value.slice(0, MAX_COLLECTION_ITEMS).map(item => serializeValue(item, depth + 1, seen));
    }

    return Object.keys(value).slice(0, MAX_COLLECTION_ITEMS).reduce((acc, key) => {
        acc[key] = SECRET_KEY_PATTERN.test(key) ? REDACTED : serializeValue(value[key], depth + 1, seen);

        return acc;
    }, {});
}

function isRepeated(signature, now) {
    const known = recentSignatures.get(signature);

    if (known && now - known.lastReportedAt < REPEAT_WINDOW_MS) {
        known.suppressed += 1;
        return { repeated: true };
    }

    if (recentSignatures.size >= MAX_TRACKED_SIGNATURES) {
        recentSignatures.clear();
    }

    recentSignatures.set(signature, { lastReportedAt: now, suppressed: 0 });

    return { repeated: false, suppressedBefore: known?.suppressed || 0 };
}

function report(event, buildPayload, signatureHint) {
    // console.error внутри обработки не должен снова попасть сюда.
    if (reporting) return;

    reporting = true;

    try {
        const now = Date.now();
        const { repeated, suppressedBefore } = isRepeated(`${event}|${signatureHint}`, now);

        if (repeated) return;

        const entry = {
            event,
            timestamp: now,
            payload: {
                ...buildPayload(),
                path: window.location.pathname,
                ...(suppressedBefore ? { suppressedBefore } : {}),
            },
        };

        if (errorListener) {
            errorListener(entry);
        } else {
            bufferedErrors = [...bufferedErrors, entry].slice(-MAX_BUFFERED_ERRORS);
        }
    } catch (error) {
        originalConsoleError('[ClientErrors] Failed to report an error', error);
    } finally {
        reporting = false;
    }
}

function handleWindowError(event) {
    const target = event.target;

    // Ошибки загрузки ресурсов не всплывают и видны только на этапе перехвата.
    if (target && target !== window) {
        const url = stripUrlQuery(target.currentSrc || target.src || target.href);

        report('resource_error', () => ({ tag: target.tagName, url }), `${target.tagName}|${url}`);
        return;
    }

    report('window_error', () => ({
        message: sanitizeString(String(event.message)),
        source: stripUrlQuery(event.filename),
        line: event.lineno,
        column: event.colno,
        error: event.error === undefined || event.error === null ? undefined : serializeValue(event.error),
    }), `${event.message}|${event.filename}|${event.lineno}|${event.colno}`);
}

function handleUnhandledRejection(event) {
    const reason = event.reason;

    report('unhandled_rejection', () => ({
        reason: serializeValue(reason),
    }), reason instanceof Error ? `${reason.name}|${reason.message}` : String(reason));
}

function handleCspViolation(event) {
    const blockedUrl = stripUrlQuery(event.blockedURI);

    report('csp_violation', () => ({
        directive: event.effectiveDirective,
        blockedUrl,
        source: stripUrlQuery(event.sourceFile),
        line: event.lineNumber,
    }), `${event.effectiveDirective}|${blockedUrl}`);
}

function handleVueError(error, instance, info) {
    report('vue_error', () => ({
        error: serializeValue(error),
        info,
        component: instance?.$options?.name || instance?.$options?.__name,
    }), `${error?.message}|${info}`);

    // Свой errorHandler отключает вывод Vue в консоль. Пишем напрямую, чтобы не задвоить запись через console.error.
    originalConsoleError(error);
}

function handleConsoleError(...args) {
    report('console_error', () => ({
        args: args.map(arg => serializeValue(arg)),
    }), args.map(arg => (arg instanceof Error ? arg.message : String(arg))).join(' ').slice(0, 200));

    originalConsoleError(...args);
}

export function installClientErrorCapture(app) {
    if (installed) return;

    installed = true;

    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    document.addEventListener('securitypolicyviolation', handleCspViolation);

    console.error = handleConsoleError;
    app.config.errorHandler = handleVueError;
}

/**
 * Лог сессии появляется только при входе в комнату, а до этого ошибки копятся в памяти.
 * Подписчик сразу получает накопленное, а все следующие ошибки — по мере появления.
 */
export function subscribeToClientErrors(listener) {
    bufferedErrors.forEach(listener);
    bufferedErrors = [];
    errorListener = listener;

    return () => {
        if (errorListener === listener) {
            errorListener = null;
        }
    };
}
